import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CreditCard,
  TestTube,
  Stethoscope,
  FileText,
  Users,
  Pill,
  Eye,
} from "lucide-react";
import { formatDate, formatDateTime } from "@/utils/dateUtils";
import { useDoctors } from "@/hooks/useDoctors";
import PrintablePrescription from "@/components/prescriptions/PrintablePrescription";

export default function PatientDetails() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);

  // Fetch patient details
  const {
    data: patientData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["patient", patientId],
    queryFn: () => apiClient.get(`/api/patients/${patientId}`),
    enabled: !!patientId,
  });

  // Fetch prescriptions for this patient
  const { data: prescriptions = [], isLoading: prescriptionsLoading } =
    useQuery({
      queryKey: ["prescriptions", "patient", patientId],
      queryFn: () =>
        apiClient.get(ENDPOINTS.PRESCRIPTIONS.BY_PATIENT(patientId)),
      enabled: !!patientId,
    });

  // Fetch doctors for name lookup
  const { data: doctors = [] } = useDoctors();

  if (isLoading) return <div className="p-6">Loading patient details...</div>;
  if (error)
    return <div className="p-6">Error loading patient: {error.message}</div>;
  if (!patientData) return <div className="p-6">Patient not found</div>;

  const { patient, bookings, bloodTests, conciergeRequests, clinicalNotes } =
    patientData;

  // Helper function to get dispense status badge color
  const getDispenseStatusBadge = (status) => {
    const statusMap = {
      pending: "bg-yellow-100 text-yellow-800",
      partially_dispensed: "bg-orange-100 text-orange-800",
      fully_dispensed: "bg-green-100 text-green-800",
      external: "bg-blue-100 text-blue-800",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800";
  };

  const handleViewPrescription = (prescription) => {
    setSelectedPrescription(prescription);
    setShowPrescriptionModal(true);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/PatientManagement")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Patients
        </Button>
        <h1 className="text-3xl font-bold">Patient Details</h1>
      </div>

      {/* Patient Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Patient Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">{patient.name}</h3>
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  {patient.email}
                </div>
                {patient.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="w-4 h-4" />
                    {patient.phone}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  ID Number
                </label>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-gray-600" />
                  <span className="font-mono">
                    {patient.identificationNumber}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Date of Birth
                </label>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <span>{formatDate(patient.dateOfBirth)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {patient.address && (
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Address
                  </label>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-600" />
                    <span>{patient.address}</span>
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Added
                </label>
                <div>{formatDate(patient.createdAt)}</div>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          {patient.emergencyContact?.name && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Emergency Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Name
                  </label>
                  <div>{patient.emergencyContact.name}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Phone
                  </label>
                  <div>{patient.emergencyContact.phone}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Relationship
                  </label>
                  <div>{patient.emergencyContact.relationship}</div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Appointments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Recent Appointments ({bookings?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {bookings?.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No appointments found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Doctor</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings?.map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>{booking.date}</TableCell>
                    <TableCell>
                      {booking.startTime} - {booking.endTime}
                    </TableCell>
                    <TableCell>{booking.serviceId}</TableCell>
                    <TableCell>{booking.doctorId}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          booking.status === "completed"
                            ? "bg-green-100 text-green-800"
                            : booking.status === "scheduled"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-red-100 text-red-800"
                        }`}
                      >
                        {booking.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Prescriptions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pill className="w-5 h-5" />
            Prescriptions ({prescriptions?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {prescriptionsLoading ? (
            <div className="text-center py-4 text-gray-500">
              Loading prescriptions...
            </div>
          ) : prescriptions?.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No prescriptions found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date Prescribed</TableHead>
                  <TableHead>Doctor</TableHead>
                  <TableHead>Dispense Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {prescriptions?.map((prescription) => (
                  <TableRow key={prescription.id}>
                    <TableCell>
                      {formatDate(prescription.prescriptionDate)}
                    </TableCell>
                    <TableCell>{prescription.doctorName || "—"}</TableCell>
                    <TableCell>
                      <Badge
                        className={getDispenseStatusBadge(
                          prescription.dispenseStatus
                        )}
                      >
                        {prescription.dispenseStatus?.replace(/_/g, " ") ||
                          "pending"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewPrescription(prescription)}
                        className="gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Blood Tests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="w-5 h-5" />
            Blood Tests ({bloodTests?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {bloodTests?.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No blood tests found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Test Type</TableHead>
                  <TableHead>Lab</TableHead>
                  <TableHead>Test Date</TableHead>
                  <TableHead>Doctor</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bloodTests?.map((test) => (
                  <TableRow key={test.id}>
                    <TableCell className="font-medium">
                      {test.testType}
                    </TableCell>
                    <TableCell>{test.labName}</TableCell>
                    <TableCell>{formatDate(test.testDate)}</TableCell>
                    <TableCell>{test.doctorName}</TableCell>
                    <TableCell>{test.notes || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Clinical Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5" />
            Clinical Notes ({clinicalNotes?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {clinicalNotes?.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No clinical notes found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Doctor</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Diagnosis</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clinicalNotes?.map((note) => (
                  <TableRow key={note.id}>
                    <TableCell>
                      {formatDate(note.date || note.visitDate)}
                    </TableCell>
                    <TableCell>{note.doctorName || note.doctor_name}</TableCell>
                    <TableCell>
                      {note.serviceName || note.service_name || "—"}
                    </TableCell>
                    <TableCell>{note.diagnosis || "—"}</TableCell>
                    <TableCell className="max-w-md">
                      <div
                        className="truncate"
                        title={
                          note.consultationNotes || note.consultation_notes
                        }
                      >
                        {note.consultationNotes ||
                          note.consultation_notes ||
                          "—"}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Concierge Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Concierge Test Requests ({conciergeRequests?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {conciergeRequests?.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No concierge requests found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Requested Tests</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Preferred Date</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conciergeRequests?.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.requestedTests?.join(", ")}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          request.status === "scheduled"
                            ? "bg-green-100 text-green-800"
                            : request.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                        }`}
                      >
                        {request.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      {request.preferredDate
                        ? formatDate(request.preferredDate)
                        : "—"}
                    </TableCell>
                    <TableCell>{formatDate(request.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Prescription Detail Modal */}
      <Dialog open={showPrescriptionModal} onOpenChange={setShowPrescriptionModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Prescription Details</DialogTitle>
          </DialogHeader>
          {selectedPrescription && (
            <PrintablePrescription
              prescription={selectedPrescription}
              doctors={doctors}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
